// RequireAuth — the auth guard. Wraps /app/* routes. While the AuthProvider
// is still loading the stored session, this renders a minimal placeholder
// so we never flash the login screen at an already-signed-in user. Once
// loading settles, unauthenticated users are sent to /login with the
// returnTo query parameter preserved. F-3: returnTo is sanitized so a
// phishing link cannot use it as an open redirect.

import { useEffect } from "react";
import type { ReactElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../services/auth";
import { safeReturnTo } from "./returnTo";

export function RequireAuth(): ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();
  useEffect(() => {
    /* no-op; placeholder does not animate */
  }, [loading]);
  if (loading && !user) {
    return (
      <div
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-[var(--color-void)] text-[var(--color-ink-mute)]"
      >
        <p className="font-subhead text-sm uppercase tracking-[0.06em]">…</p>
      </div>
    );
  }
  if (!user) {
    // F-3: only `/app/...` paths are accepted; everything else
    // collapses to the canonical post-login target.
    const returnTo = safeReturnTo(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Outlet />;
}
